import { useEffect, useRef, useState } from "react";
import { Mail, ShieldCheck, Store } from "lucide-react";
import { toast } from "sonner";
import { Footer } from "@/components/lunar/Footer";
import { Logo } from "@/components/lunar/Logo";
import { Reveal } from "@/components/lunar/Reveal";

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls =
    "w-full rounded-xl border border-[#C2C0B8] bg-transparent px-4 py-3 text-sm text-[#1D201A] placeholder:text-[#5C5F58] focus:border-[#4A5D23] focus:outline-none";
const labelCls = "mb-1.5 block text-xs font-medium uppercase tracking-[0.14em] text-[#5C5F58]";

const INFO = [
    {
        icon: Mail,
        title: "General support",
        copy: "Questions about your account, the app or something not working as expected.",
        link: { label: "hello@somerhillgroup.com", href: "mailto:hello@somerhillgroup.com", testId: "support-email-link" },
    },
    {
        icon: ShieldCheck,
        title: "Safety concerns",
        copy: "Read our safety guidance or report a concern about another user.",
        link: { label: "Visit Safety", href: "/safety", testId: "support-safety-link" },
    },
    {
        icon: Store,
        title: "Venues and events",
        copy: "For venue kits, events and partnership enquiries, use the relevant page so we have the right details.",
        link: { label: "Lunar for Venues", href: "/venue-kit", testId: "support-venues-link" },
    },
];

export default function Support() {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [status, setStatus] = useState("idle");
    const confirmationRef = useRef(null);
    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    useEffect(() => {
        document.title = "Support — Lunar";
        return () => {
            document.title = "Lunar — The social network for real life";
        };
    }, []);

    useEffect(() => {
        if (status !== "sent") return;
        confirmationRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        confirmationRef.current?.focus({ preventScroll: true });
    }, [status]);

    const submit = async (e) => {
        e.preventDefault();
        const payload = {
            name: form.name.trim(),
            email: form.email.trim(),
            message: form.message.trim(),
        };
        if (!payload.name || !payload.email || !payload.message) {
            toast.error("Please complete all fields before sending.");
            return;
        }
        setStatus("sending");
        try {
            const res = await fetch(`${API}/api/support-request`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error("send failed");
            toast.dismiss();
            setStatus("sent");
        } catch {
            setStatus("idle");
            toast.error("Something went wrong sending your message — please try again.");
        }
    };

    return (
        <div data-testid="support-page">
            <header className="mx-auto flex max-w-6xl items-center justify-between px-6 pt-7">
                <a href="/" aria-label="Back to Lunar home" data-testid="support-back-home">
                    <Logo size={38} />
                </a>
                <a
                    href="/"
                    data-testid="support-home-link"
                    className="rounded-full border border-[#C2C0B8] px-4 py-2 text-xs font-medium text-[#3E413B] transition-[border-color,color,transform] duration-300 hover:-translate-y-[1px] hover:border-[#899B6A] hover:text-[#2C3618]"
                >
                    Home
                </a>
            </header>

            <main className="mx-auto max-w-6xl px-6 pb-24 pt-16 sm:pt-20">
                <Reveal className="max-w-3xl">
                    <p className="text-[11px] font-medium uppercase tracking-[0.28em] text-[#899B6A]">
                        Support
                    </p>
                    <h1
                        data-testid="support-heading"
                        className="font-display mt-5 text-4xl font-medium leading-[1.08] tracking-tight text-[#1D201A] sm:text-6xl"
                    >
                        How can we help?
                    </h1>
                    <p
                        data-testid="support-intro"
                        className="mt-6 max-w-2xl text-base leading-relaxed text-[#3E413B] sm:text-lg"
                    >
                        Send us a message and we’ll get back to you. If your concern is about
                        safety, please use the Safety page so you can find the right guidance first.
                    </p>
                </Reveal>

                <div data-testid="support-info-cards" className="mt-12 grid gap-4 md:grid-cols-3">
                    {INFO.map(({ icon: Icon, title, copy, link }, i) => (
                        <Reveal key={title} delay={i * 0.06}>
                            <section
                                data-testid={`support-info-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="h-full rounded-3xl border border-[#E5E3DC] bg-[#F9F8F4] p-6"
                            >
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#EEF1E5]">
                                    <Icon className="h-5 w-5 text-[#4A5D23]" strokeWidth={1.75} />
                                </div>
                                <h2 className="font-display mt-5 text-2xl font-semibold text-[#1D201A]">
                                    {title}
                                </h2>
                                <p className="mt-3 text-sm leading-relaxed text-[#3E413B]">{copy}</p>
                                <a
                                    href={link.href}
                                    data-testid={link.testId}
                                    className="mt-4 inline-flex text-sm font-bold text-[#2C3618] underline decoration-[#899B6A] underline-offset-4 transition-colors hover:text-[#4A5D23]"
                                >
                                    {link.label}
                                </a>
                            </section>
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={0.15}>
                    <div className="mt-14 rounded-3xl border border-[#E5E3DC] bg-[#F0EFEB] p-6 sm:p-10">
                        {status === "sent" ? (
                            <div
                                ref={confirmationRef}
                                tabIndex={-1}
                                role="status"
                                aria-live="polite"
                                data-testid="support-confirmation"
                                className="rounded-3xl border border-[#899B6A] bg-[#D5D8CB]/40 p-10 text-center focus:outline-none sm:p-14"
                            >
                                <h2 className="font-display text-3xl font-medium text-[#2C3618] sm:text-4xl">
                                    Thanks — we’ve received your message.
                                </h2>
                                <p className="mt-4 text-base text-[#3E413B]">
                                    We’ll get back to you as soon as we can.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="grid gap-5" data-testid="support-form">
                                <div className="grid gap-5 sm:grid-cols-2">
                                    <div>
                                        <label className={labelCls} htmlFor="support-name">
                                            Name <span className="text-[#4A5D23]">*</span>
                                        </label>
                                        <input
                                            id="support-name"
                                            required
                                            data-testid="support-form-name"
                                            className={inputCls}
                                            value={form.name}
                                            onChange={set("name")}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelCls} htmlFor="support-email">
                                            Email <span className="text-[#4A5D23]">*</span>
                                        </label>
                                        <input
                                            id="support-email"
                                            required
                                            type="email"
                                            data-testid="support-form-email"
                                            className={inputCls}
                                            value={form.email}
                                            onChange={set("email")}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className={labelCls} htmlFor="support-message">
                                        Message <span className="text-[#4A5D23]">*</span>
                                    </label>
                                    <textarea
                                        id="support-message"
                                        required
                                        rows={6}
                                        data-testid="support-form-message"
                                        className={inputCls}
                                        placeholder="Tell us what you need help with…"
                                        value={form.message}
                                        onChange={set("message")}
                                    />
                                </div>
                                <div>
                                    <button
                                        type="submit"
                                        data-testid="support-form-submit"
                                        disabled={status === "sending"}
                                        className="w-full rounded-full bg-[#2C3618] py-4 text-sm font-bold text-[#F9F8F4] transition-[background-color,transform] duration-300 hover:-translate-y-[2px] hover:bg-[#4A5D23] disabled:opacity-60 sm:w-auto sm:px-12"
                                    >
                                        {status === "sending" ? "Sending…" : "Send message"}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </Reveal>
            </main>
            <Footer />
        </div>
    );
}
